import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Lock, User, AlertCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9645c0b6-481f-4d07-81fb-c2ea43b51b14/artifacts/rw309x94_VoidWolves2-Photoroom.png";

export const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(username, password);
            toast.success('Login successful!');
            navigate('/admin');
        } catch (err) {
            const message = err.response?.data?.detail || 'Authentication failed';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center px-4" data-testid="login-page">
            <Toaster position="top-right" theme="dark" />
            
            <div className="w-full max-w-md">
                <div className="tactical-panel p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <img 
                            src={LOGO_URL} 
                            alt="Voidwolves" 
                            className="w-20 mx-auto mb-4 opacity-80"
                        />
                        <h1 className="font-heading font-bold text-2xl text-white uppercase">
                            Admin Access
                        </h1>
                        <p className="font-mono text-xs text-gray-500 mt-2 tracking-wider">
                            SECURE AUTHENTICATION PORTAL
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 mb-6 bg-red-500/10 border border-red-500/30" data-testid="login-error">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-sm text-red-400">{error}</span>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                        <div>
                            <label className="form-label">Username</label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    className="form-input pl-10"
                                    placeholder="Enter username"
                                    data-testid="input-username"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="form-label">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="form-input pl-10"
                                    placeholder="Enter password"
                                    data-testid="input-password"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full py-4 flex items-center justify-center gap-2"
                            data-testid="submit-login-button"
                        >
                            <Shield className="w-4 h-4" />
                            {loading ? 'Processing...' : 'Authenticate'}
                        </button>
                    </form>

                    <p className="mt-6 text-center font-mono text-xs text-gray-600">
                        Contact an existing admin to get credentials
                    </p>
                </div>
            </div>
        </div>
    );
};
